const setTitle = (title) => (title ? `${title} | DeeCRM360` : "DeeCRM360");

const authRoutes = [
  {
    path: "/auth/sign-in",
    name: "auth.sign-in",
    meta: { title: setTitle("Sign In") },
    component: () => import("@/views/auth/login.vue"),
  },
  {
    path: "/auth/reset-pass",
    name: "auth.reset-pass",
    meta: { title: setTitle("Reset Password") },
    component: () => import("@/views/auth/reset-pass.vue"),
  },
  {
    // Shown after asking for a reset link, whether or not the address has an account.
    path: "/auth/forgot-success",
    name: "auth.forgot.success",
    meta: { title: setTitle("Check Your Email") },
    component: () => import("@/views/auth/forgot-pass-success.vue"),
  },
  {
    // Where the emailed reset link lands: /auth/new-password?token=…&email=…
    path: "/auth/new-password/:node?",
    name: "auth.new-password",
    meta: { title: setTitle("New Password") },
    component: () => import("@/views/auth/new-password.vue"),
  },
  {
    path: "/set-password/:id",
    name: "auth.set-password",
    meta: { title: setTitle("Set Password") },
    component: () => import("@/views/auth/set-password.vue"),
  },
  {
    path: "/invite/accept",
    name: "auth.invite-accept",
    meta: { title: setTitle("Activate Account") },
    component: () => import("@/views/auth/invite-accept.vue"),
  },
];
// The page a customer answers a survey on: reached from a single-use link, no sign-in.
const publicRoutes = [
  {
    path: "/s/:token",
    name: "surveys.respond",
    meta: { title: setTitle("Survey") },
    component: () => import("@/views/surveys/respond.vue"),
  },
];
const errorRoutes = [
  {
    path: "/auth/error-404",
    name: "error.404",
    meta: { title: setTitle("Error 404") },
    component: () => import("@/views/auth/error-404.vue"),
  },
  {
    path: "/auth/error-500",
    name: "error.500",
    meta: { title: setTitle("Error 500") },
    component: () => import("@/views/auth/error-500.vue"),
  },
  {
    path: "/auth/error-403",
    name: "error.403",
    meta: { title: setTitle("Error 403") },
    component: () => import("@/views/auth/error-403.vue"),
  },
  { path: "/:catchAll(.*)", redirect: "/auth/error-404" },
];

// Home, plus each person's own queue of work.
const dashboardRoutes = [
  {
    path: "/",
    name: "dashboards.home",
    meta: { title: setTitle("Dashboard"), authRequired: true },
    component: () => import("@/views/dashboards/index.vue"),
  },
  {
    path: "/my-work",
    name: "dashboards.my-work",
    meta: { title: setTitle("My Work"), authRequired: true },
    component: () => import("@/views/dashboards/my-work.vue"),
  },
  {
    path: "/admin/dashboard",
    name: "dashboards.admin",
    meta: { title: setTitle("Dashboard"), authRequired: true, permission: "tenants.view" },
    component: () => import("@/views/administration/tenants/dashboard.vue"),
  },
  {
    path: "/administration/logs",
    name: "administration.logs",
    meta: { title: setTitle("System Logs"), authRequired: true, permission: "logs.view" },
    component: () => import("@/views/administration/logs/index.vue"),
  },
];

// P3: organisations identified before they become qualified leads.
const prospectRoutes = [
  {
    path: "/prospects",
    name: "prospects.list",
    meta: { title: setTitle("Prospects"), authRequired: true, permission: "prospects.view" },
    component: () => import("@/views/prospects/index.vue"),
  },
  {
    path: "/prospects/create",
    name: "prospects.create",
    meta: { title: setTitle("New Prospect"), authRequired: true, permission: "prospects.create" },
    component: () => import("@/views/prospects/create.vue"),
  },
  {
    path: "/prospects/pool",
    name: "prospects.pool",
    meta: { title: setTitle("Prospect Pool"), authRequired: true, permission: "prospects.view" },
    component: () => import("@/views/prospects/pool.vue"),
  },
  {
    path: "/prospects/disqualified",
    name: "prospects.disqualified",
    meta: { title: setTitle("Disqualified & Recycle"), authRequired: true, permission: "prospects.view" },
    component: () => import("@/views/prospects/disqualified.vue"),
  },
  {
    path: "/prospects/import",
    name: "prospects.import",
    meta: { title: setTitle("Import Prospects"), authRequired: true, permission: "prospects.import" },
    component: () => import("@/views/prospects/import.vue"),
  },
  {
    path: "/prospects/:id",
    name: "prospects.show",
    meta: { title: setTitle("Prospect"), authRequired: true, permission: "prospects.view" },
    component: () => import("@/views/prospects/show.vue"),
  },
];

// P4: leads, opportunities and the sales pipeline.
const salesRoutes = [
  {
    path: "/leads",
    name: "leads.list",
    meta: { title: setTitle("Leads"), authRequired: true, permission: "leads.view" },
    component: () => import("@/views/leads/index.vue"),
  },
  {
    path: "/leads/:id",
    name: "leads.show",
    meta: { title: setTitle("Lead"), authRequired: true, permission: "leads.view" },
    component: () => import("@/views/leads/show.vue"),
  },
  {
    path: "/pipeline",
    name: "opportunities.pipeline",
    meta: { title: setTitle("Sales Pipeline"), authRequired: true, permission: "opportunities.view" },
    component: () => import("@/views/opportunities/pipeline.vue"),
  },
  {
    path: "/opportunities",
    name: "opportunities.list",
    meta: { title: setTitle("Opportunities"), authRequired: true, permission: "opportunities.view" },
    component: () => import("@/views/opportunities/index.vue"),
  },
  {
    path: "/opportunities/lost",
    name: "opportunities.lost",
    meta: { title: setTitle("Lost Deals"), authRequired: true, permission: "opportunities.view" },
    component: () => import("@/views/opportunities/lost.vue"),
  },
  {
    path: "/opportunities/:id",
    name: "opportunities.show",
    meta: { title: setTitle("Opportunity"), authRequired: true, permission: "opportunities.view" },
    component: () => import("@/views/opportunities/show.vue"),
  },
  {
    path: "/quotations",
    name: "quotations.list",
    meta: { title: setTitle("Proposals & Quotations"), authRequired: true, permission: "quotations.view" },
    component: () => import("@/views/quotations/index.vue"),
  },
  {
    path: "/quotations/:id",
    name: "quotations.show",
    meta: { title: setTitle("Quotation"), authRequired: true, permission: "quotations.view" },
    component: () => import("@/views/quotations/show.vue"),
  },
  {
    path: "/demos",
    name: "demos.list",
    meta: { title: setTitle("Demos"), authRequired: true, permission: "demos.view" },
    component: () => import("@/views/demos/index.vue"),
  },
];

// P5/P6: customers, their contacts and what they have signed for.
const customerRoutes = [
  {
    path: "/customers",
    name: "customers.list",
    meta: { title: setTitle("Customers"), authRequired: true, permission: "customers.view" },
    component: () => import("@/views/customers/index.vue"),
  },
  {
    path: "/customers/:id",
    name: "customers.show",
    meta: { title: setTitle("Customer 360"), authRequired: true, permission: "customers.view" },
    component: () => import("@/views/customers/show.vue"),
  },
  {
    path: "/contacts",
    name: "contacts.list",
    meta: { title: setTitle("Contacts"), authRequired: true, permission: "customers.view" },
    component: () => import("@/views/contacts/index.vue"),
  },
  {
    path: "/contracts",
    name: "contracts.list",
    meta: { title: setTitle("Contracts & Subscriptions"), authRequired: true, permission: "contracts.view" },
    component: () => import("@/views/contracts/index.vue"),
  },
  {
    path: "/contracts/:id",
    name: "contracts.show",
    meta: { title: setTitle("Contract"), authRequired: true, permission: "contracts.view" },
    component: () => import("@/views/contracts/show.vue"),
  },
];

// P5: from signed contract to go-live and handover to Customer Success.
const onboardingRoutes = [
  {
    path: "/onboarding",
    name: "onboarding.list",
    meta: { title: setTitle("Onboarding"), authRequired: true, permission: "onboarding.view" },
    component: () => import("@/views/onboarding/index.vue"),
  },
  {
    path: "/onboarding/:id",
    name: "onboarding.show",
    meta: { title: setTitle("Onboarding Project"), authRequired: true, permission: "onboarding.view" },
    component: () => import("@/views/onboarding/show.vue"),
  },
];

// P6: looking after live customers.
const successRoutes = [
  {
    path: "/success/health",
    name: "success.health",
    meta: { title: setTitle("Customer Health"), authRequired: true, permission: "success.view" },
    component: () => import("@/views/success/health.vue"),
  },
  {
    path: "/success/engagements",
    name: "success.engagements",
    meta: { title: setTitle("Engagements"), authRequired: true, permission: "engagements.view" },
    component: () => import("@/views/success/engagements.vue"),
  },
  {
    path: "/success/feedback",
    name: "success.feedback",
    meta: { title: setTitle("Customer Feedback"), authRequired: true, permission: "feedback.view" },
    component: () => import("@/views/success/feedback.vue"),
  },
  {
    path: "/success/surveys",
    name: "surveys.list",
    meta: { title: setTitle("Surveys"), authRequired: true, permission: "surveys.view" },
    component: () => import("@/views/surveys/index.vue"),
  },
  {
    path: "/success/surveys/:id",
    name: "surveys.show",
    meta: { title: setTitle("Survey"), authRequired: true, permission: "surveys.view" },
    component: () => import("@/views/surveys/show.vue"),
  },
  {
    path: "/success/expansion",
    name: "success.expansion",
    meta: { title: setTitle("Upsell & Cross-sell"), authRequired: true, permission: "success.view" },
    component: () => import("@/views/success/expansion.vue"),
  },
  {
    path: "/support/raise",
    name: "support.raise",
    meta: { title: setTitle("Raise a Ticket"), authRequired: true, permission: "tickets.raise" },
    component: () => import("@/views/support/raise.vue"),
  },
  {
    path: "/support/my-requests",
    name: "support.my-requests",
    meta: { title: setTitle("My Requests"), authRequired: true, permission: "tickets.raise" },
    component: () => import("@/views/support/my-requests.vue"),
  },
  {
    path: "/support/tickets",
    name: "support.tickets",
    meta: { title: setTitle("Support Tickets"), authRequired: true, permission: "tickets.view" },
    component: () => import("@/views/support/tickets.vue"),
  },
  {
    path: "/support/tickets/:id",
    name: "support.ticket",
    // A requester opens their own request here (and a team member their queue's); the API decides which.
    meta: { title: setTitle("Ticket"), authRequired: true, permission: "tickets.view|tickets.raise" },
    component: () => import("@/views/support/ticket.vue"),
  },
  {
    path: "/support/connected-apps",
    name: "support.apps",
    meta: { title: setTitle("Client Connections"), authRequired: true, permission: "ticket-clients.view" },
    component: () => import("@/views/support/apps.vue"),
  },
  {
    path: "/support/connected-apps/:id",
    name: "support.app",
    meta: { title: setTitle("Client Connection"), authRequired: true, permission: "ticket-clients.view" },
    component: () => import("@/views/support/app.vue"),
  },
  {
    path: "/support/complaints",
    name: "support.complaints",
    meta: { title: setTitle("Complaints"), authRequired: true, permission: "complaints.view" },
    component: () => import("@/views/support/complaints.vue"),
  },
  {
    path: "/renewals",
    name: "renewals.list",
    meta: { title: setTitle("Renewals"), authRequired: true, permission: "renewals.view" },
    component: () => import("@/views/renewals/index.vue"),
  },
];

// P7: churn is managed while the customer can still be kept; exit once they have decided to go.
const retentionRoutes = [
  {
    path: "/retention/risk",
    name: "retention.risk",
    meta: { title: setTitle("Churn Risk"), authRequired: true, permission: "churn.view" },
    component: () => import("@/views/retention/risk.vue"),
  },
  {
    path: "/retention/exits",
    name: "retention.exits",
    meta: { title: setTitle("Customer Exits"), authRequired: true, permission: "exits.view" },
    component: () => import("@/views/retention/exits.vue"),
  },
  {
    path: "/retention/exits/:id",
    name: "retention.exit",
    meta: { title: setTitle("Customer Exit"), authRequired: true, permission: "exits.view" },
    component: () => import("@/views/retention/exit.vue"),
  },
  {
    path: "/retention/win-back",
    name: "retention.win-back",
    meta: { title: setTitle("Win-back"), authRequired: true, permission: "churn.view" },
    component: () => import("@/views/retention/win-back.vue"),
  },
];

// P2: the shared task, follow-up and approval engine.
const activityRoutes = [
  {
    path: "/activities/tasks",
    name: "activities.tasks",
    meta: { title: setTitle("Tasks & Follow-ups"), authRequired: true, permission: "tasks.view" },
    component: () => import("@/views/activities/tasks.vue"),
  },
  {
    path: "/activities/calendar",
    name: "activities.calendar",
    meta: { title: setTitle("Calendar"), authRequired: true, permission: "tasks.view" },
    component: () => import("@/views/activities/calendar.vue"),
  },
  {
    path: "/approvals",
    name: "approvals.list",
    meta: { title: setTitle("Approvals"), authRequired: true, permission: "approvals.view" },
    component: () => import("@/views/approvals/index.vue"),
  },
];

// P8: reports read the same controlled data the screens do.
const reportRoutes = [
  {
    path: "/reports",
    name: "reports.catalogue",
    meta: { title: setTitle("Reports"), authRequired: true, permission: "reports.view" },
    component: () => import("@/views/reports/index.vue"),
  },
  {
    path: "/reports/funnel",
    name: "reports.funnel",
    meta: { title: setTitle("Conversion & Drop-off"), authRequired: true, permission: "reports.view" },
    component: () => import("@/views/reports/funnel.vue"),
  },
  {
    path: "/reports/sales",
    name: "reports.sales",
    meta: { title: setTitle("Sales Performance"), authRequired: true, permission: "reports.view" },
    component: () => import("@/views/reports/sales.vue"),
  },
  {
    path: "/reports/customers",
    name: "reports.customers",
    meta: { title: setTitle("Customer Lifecycle"), authRequired: true, permission: "reports.view" },
    component: () => import("@/views/reports/customers.vue"),
  },
  {
    path: "/reports/support",
    name: "reports.support",
    meta: { title: setTitle("Support Analytics"), authRequired: true, permission: "reports.view" },
    component: () => import("@/views/reports/support.vue"),
  },
  {
    path: "/reports/kpis",
    name: "reports.kpis",
    meta: { title: setTitle("KPI Catalogue"), authRequired: true, permission: "kpis.view" },
    component: () => import("@/views/reports/kpis.vue"),
  },
];

const documentRoutes = [
  {
    path: "/documents/browse",
    name: "documents.browse",
    meta: { title: setTitle("Browse Documents"), authRequired: true, permission: "documents.view" },
    component: () => import("@/views/documents/browse/index.vue"),
  },
  {
    path: "/documents/browse/:folderId",
    name: "documents.browse.folder",
    meta: { title: setTitle("Browse Documents"), authRequired: true, permission: "documents.view" },
    component: () => import("@/views/documents/browse/index.vue"),
  },
  {
    path: "/documents/categories",
    name: "documents.categories",
    meta: { title: setTitle("Document Categories"), authRequired: true, permission: "documents.view" },
    component: () => import("@/views/documents/categories/index.vue"),
  },
  {
    path: "/documents/all",
    name: "documents.all",
    meta: { title: setTitle("All Documents"), authRequired: true, permission: "documents.view" },
    component: () => import("@/views/documents/all-documents/index.vue"),
  },
];

// Everything a company can shape to the way it sells and serves, kept per company.
const settingsRoutes = [
  {
    path: "/company",
    name: "company.profile",
    meta: { title: setTitle("Company Profile"), authRequired: true, permission: "company.view" },
    component: () => import("@/views/company/index.vue"),
  },
  {
    path: "/settings/modules",
    name: "settings.modules",
    meta: { title: setTitle("Modules & Features"), authRequired: true, permission: "settings.manage" },
    component: () => import("@/views/settings/modules.vue"),
  },
  {
    path: "/settings/organisation",
    name: "settings.organisation",
    meta: { title: setTitle("Departments, Teams & Staff"), authRequired: true, permission: "settings.view" },
    component: () => import("@/views/settings/organisation.vue"),
  },
  {
    path: "/settings/territories",
    name: "settings.territories",
    meta: { title: setTitle("Locations & Territories"), authRequired: true, permission: "settings.view" },
    component: () => import("@/views/settings/territories.vue"),
  },
  {
    path: "/settings/products",
    name: "settings.products",
    meta: { title: setTitle("Products & Packages"), authRequired: true, permission: "settings.view|products.manage" },
    component: () => import("@/views/settings/products.vue"),
  },
  {
    path: "/settings/stages",
    name: "settings.stages",
    meta: { title: setTitle("Stages & Conversion Rules"), authRequired: true, permission: "settings.view" },
    component: () => import("@/views/settings/stages.vue"),
  },
  {
    path: "/settings/reason-codes",
    name: "settings.reason-codes",
    meta: { title: setTitle("Reason Codes"), authRequired: true, permission: "settings.view" },
    component: () => import("@/views/settings/reason-codes.vue"),
  },
  {
    path: "/settings/lookups",
    name: "settings.lookups",
    meta: { title: setTitle("Lists & Lookups"), authRequired: true, permission: "settings.view" },
    component: () => import("@/views/settings/lookups.vue"),
  },
  {
    path: "/settings/custom-fields",
    name: "settings.custom-fields",
    meta: { title: setTitle("Custom Fields"), authRequired: true, permission: "settings.view" },
    component: () => import("@/views/settings/custom-fields.vue"),
  },
  {
    path: "/settings/ticket-types",
    name: "settings.ticket-types",
    meta: { title: setTitle("Ticket Types & Phases"), authRequired: true, permission: "settings.view|ticket-types.configure" },
    component: () => import("@/views/settings/ticket-types.vue"),
  },
  {
    path: "/settings/sla",
    name: "settings.sla",
    meta: { title: setTitle("SLA Policies"), authRequired: true, permission: "settings.view" },
    component: () => import("@/views/settings/sla.vue"),
  },
  {
    path: "/settings/onboarding-templates",
    name: "settings.onboarding-templates",
    meta: { title: setTitle("Onboarding Templates"), authRequired: true, permission: "settings.manage" },
    component: () => import("@/views/settings/onboarding-templates.vue"),
  },
  {
    path: "/settings/customer-success",
    name: "settings.customer-success",
    meta: { title: setTitle("Customer Success Rules"), authRequired: true, permission: "settings.view|success.view|health.configure" },
    component: () => import("@/views/settings/customer-success.vue"),
  },
  {
    path: "/settings/notifications",
    name: "settings.notifications",
    meta: { title: setTitle("Notifications & Escalations"), authRequired: true, permission: "settings.manage" },
    component: () => import("@/views/settings/notifications.vue"),
  },
  {
    path: "/settings/numbering",
    name: "settings.numbering",
    meta: { title: setTitle("Numbering"), authRequired: true, permission: "settings.view" },
    component: () => import("@/views/settings/numbering.vue"),
  },
  {
    path: "/settings/integrations",
    name: "settings.integrations",
    meta: { title: setTitle("Integrations"), authRequired: true, permission: "integrations.view" },
    component: () => import("@/views/settings/integrations.vue"),
  },
  {
    path: "/configuration/approval-matrix",
    name: "config.approval-matrix",
    meta: { title: setTitle("Approval Matrix"), authRequired: true, permission: "approval-workflows.view" },
    component: () => import("@/views/config/approval-matrix/index.vue"),
  },
];

// P0: who can sign in, what they may do, and which records they may see.
const systemAdministrationRoutes = [
  {
    path: "/system-administration/users",
    name: "system-administration.users",
    meta: { title: setTitle("Users"), authRequired: true, permission: "users.view" },
    component: () => import("@/views/system-administration/users.vue"),
  },
  {
    path: "/system-administration/roles",
    name: "system-administration.roles",
    meta: { title: setTitle("Roles"), authRequired: true, permission: "roles.view" },
    component: () => import("@/views/system-administration/roles/index.vue"),
  },
  {
    path: "/system-administration/role-permissions",
    name: "system-administration.role-permissions",
    meta: { title: setTitle("Role Permissions"), authRequired: true, permission: "role-permissions.view" },
    component: () => import("@/views/system-administration/role-permissions/index.vue"),
  },
  {
    path: "/system-administration/role-assignments",
    name: "system-administration.role-assignments",
    meta: { title: setTitle("Role Assignment"), authRequired: true, permission: "role-assignments.view" },
    component: () => import("@/views/system-administration/role-assignments/index.vue"),
  },
  {
    path: "/system-administration/data-access",
    name: "system-administration.data-access",
    meta: { title: setTitle("Data Access Scope"), authRequired: true, permission: "access.view" },
    component: () => import("@/views/system-administration/data-access.vue"),
  },
  {
    path: "/system-administration/sensitive-fields",
    name: "config.sensitive-fields",
    meta: { title: setTitle("Sensitive Fields"), authRequired: true, permission: "sensitive-fields.manage" },
    component: () => import("@/views/config/sensitive-fields/index.vue"),
  },
  {
    path: "/system-administration/temporary-access",
    name: "system-administration.temporary-access",
    meta: { title: setTitle("Temporary Access"), authRequired: true, permission: "access.view" },
    component: () => import("@/views/system-administration/temporary-access.vue"),
  },
  {
    path: "/system-administration/security",
    name: "system-administration.security",
    meta: { title: setTitle("Security Policy"), authRequired: true, permission: "security.manage" },
    component: () => import("@/views/system-administration/security.vue"),
  },
  {
    path: "/system-administration/migration",
    name: "system-administration.migration",
    meta: { title: setTitle("Data Migration"), authRequired: true, permission: "migration.manage" },
    component: () => import("@/views/system-administration/migration.vue"),
  },
  {
    path: "/activity-logs",
    name: "logs.activity",
    meta: { title: setTitle("Activity Logs"), authRequired: true, permission: "logs.view" },
    component: () => import("@/views/administration/logs/index.vue"),
  },
];

const dataRoutes = [
  {
    path: "/exports",
    name: "exports.list",
    meta: { title: setTitle("My Exports"), authRequired: true, permission: "exports.view" },
    component: () => import("@/views/exports/index.vue"),
  },
  {
    path: "/exports/:id",
    name: "exports.show",
    meta: { title: setTitle("My Exports"), authRequired: true, permission: "exports.view" },
    component: () => import("@/views/exports/index.vue"),
  },
  {
    path: "/imports",
    name: "imports.list",
    meta: { title: setTitle("My Imports"), authRequired: true },
    component: () => import("@/views/imports/index.vue"),
  },
  {
    path: "/imports/:id",
    name: "imports.show",
    meta: { title: setTitle("My Imports"), authRequired: true },
    component: () => import("@/views/imports/index.vue"),
  },
];

// The platform admin: the companies using the CRM and how they are billed.
const administrationRoutes = [
  {
    path: "/administration/tenants",
    name: "administration.tenants.list",
    meta: { title: setTitle("Manage Tenants"), authRequired: true, permission: "tenants.view" },
    component: () => import("@/views/administration/tenants/index.vue"),
  },
  {
    path: "/administration/tenants/create",
    name: "administration.tenants.create",
    meta: { title: setTitle("Create Tenant"), authRequired: true, permission: "tenants.create" },
    component: () => import("@/views/administration/tenants/create.vue"),
  },
  {
    path: "/administration/tenants/:id",
    name: "administration.tenants.show",
    meta: { title: setTitle("Tenant"), authRequired: true, permission: "tenants.view" },
    component: () => import("@/views/administration/tenants/show.vue"),
  },
  {
    path: "/administration/client-usage",
    name: "administration.client-usage",
    meta: { title: setTitle("Client Usage"), authRequired: true, permission: "client-usage.view" },
    component: () => import("@/views/administration/client-usage/index.vue"),
  },
  {
    path: "/administration/client-usage/:id",
    name: "administration.client-usage.company",
    meta: { title: setTitle("Company Usage"), authRequired: true, permission: "client-usage.view" },
    component: () => import("@/views/administration/client-usage/company.vue"),
  },
  {
    path: "/administration/admins",
    name: "administration.admins.list",
    meta: { title: setTitle("Admin Users"), authRequired: true, permission: "admins.view" },
    component: () => import("@/views/administration/admins/index.vue"),
  },
  {
    path: "/administration/admin-roles",
    name: "administration.admin-roles",
    meta: { title: setTitle("Roles and rights"), authRequired: true, permission: "admin-roles.view" },
    component: () => import("@/views/administration/admin-roles/index.vue"),
  },
  {
    path: "/administration/api-docs-access",
    name: "administration.api-docs-access",
    meta: { title: setTitle("API Documentation Access"), authRequired: true, permission: "api-docs.view" },
    component: () => import("@/views/administration/api-docs-access/index.vue"),
  },
  {
    path: "/administration/catalogue",
    name: "administration.catalogue",
    meta: { title: setTitle("Modules Catalogue"), authRequired: true, permission: "catalogue.view" },
    component: () => import("@/views/administration/catalogue/index.vue"),
  },
  {
    path: "/administration/integrations",
    name: "administration.integrations",
    meta: { title: setTitle("Integrations"), authRequired: true, permission: "platform-integrations.view" },
    component: () => import("@/views/administration/integrations/index.vue"),
  },
  {
    path: "/administration/billing/plans",
    name: "administration.billing.plans",
    meta: { title: setTitle("Plans"), authRequired: true, permission: "plans.view" },
    component: () => import("@/views/administration/billing/plans.vue"),
  },
  {
    path: "/administration/billing/methods",
    name: "administration.billing.methods",
    meta: { title: setTitle("Payment Methods"), authRequired: true, permission: "billing.view" },
    component: () => import("@/views/administration/billing/methods.vue"),
  },
  {
    path: "/administration/billing/schedules",
    name: "administration.billing.schedules",
    meta: { title: setTitle("Payment Schedules"), authRequired: true, permission: "billing.view" },
    component: () => import("@/views/administration/billing/schedules.vue"),
  },
  {
    path: "/administration/trial-applications",
    name: "administration.trial-applications",
    meta: { title: setTitle("Trial Applications"), authRequired: true, permission: "trial-applications.view" },
    component: () => import("@/views/administration/trial-applications/index.vue"),
  },
  {
    path: "/administration/website",
    name: "administration.website",
    meta: { title: setTitle("Website Settings"), authRequired: true, permission: "website.view" },
    component: () => import("@/views/administration/website/index.vue"),
  },
  {
    path: "/administration/website-traffic",
    name: "administration.website-traffic",
    meta: { title: setTitle("Website Traffic"), authRequired: true, permission: "website-traffic.view" },
    component: () => import("@/views/administration/website-traffic/index.vue"),
  },
];

// Specific paths come before the :id ones in each group, so /prospects/pool is never read as a prospect.
export const allRoute = [
  ...authRoutes,
  ...publicRoutes,
  ...errorRoutes,
  ...dashboardRoutes,
  ...prospectRoutes,
  ...salesRoutes,
  ...customerRoutes,
  ...onboardingRoutes,
  ...successRoutes,
  ...retentionRoutes,
  ...activityRoutes,
  ...reportRoutes,
  ...documentRoutes,
  ...settingsRoutes,
  ...systemAdministrationRoutes,
  ...dataRoutes,
  ...administrationRoutes,
];
